var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { CustomElement, P, Watch, PropertyConverters, Utils, CustomEventUtils, avoidHandler, Behaviors, PropertyChangeEventName } from '@pacem/pacem-core';
import { Geometry } from '@pacem/pacem-numerical';
import { isCamera, StalePropertyFlag } from '../../../types';
import { TAG_MIDDLE_NAME } from '../../../constants';
import { BooleanOrNumberConverter } from '../../../converters';
//namespace Pacem.Components.Drawing3D {
const Vector3D = Geometry.LinearAlgebra.Vector3D;
const Matrix3D = Geometry.LinearAlgebra.Matrix3D;
const Quaternion = Geometry.LinearAlgebra.Quaternion;
const Spherical = Geometry.LinearAlgebra.Spherical;
function findCameraStage(element) {
    if (isCamera(element)) {
        return element.stage;
    }
    return null;
}
const ROTATION_FACTOR = 1, ZOOM_FACTOR = 1, FRICTION_FACTOR = .05, EPSILON = 0.001;
const DEFAULT_CONFIGURATION = {
    maxAzimuth: 180, minAzimuth: -180, inertia: false, maxPolar: Infinity, minPolar: -Infinity,
    zoom: { enabled: true }, pan: { enabled: true, button: 2 }, rotation: { enabled: true, button: 0 }
};
export class OrbitCameraBehavior {
    constructor(item, _config) {
        this._config = _config;
        /** State of the current configuration, obtained from the interaction inputs. */
        this._input = {
            /** Zoom/scale */
            rho: 0,
            /** Azimuth (rotation around x axis in webgpu) */
            theta: 0,
            /** Polar (rotation around y axis in webgpu) */
            phi: 0,
            /** pan/offset x*/
            x: 0,
            /** pan/offset y*/
            y: 0,
        };
        /** Sibling state of {@link _input} that comes helpful when inertia is involved. */
        this._progress = {
            rho: 0,
            theta: 0,
            phi: 0,
            x: 0,
            y: 0,
        };
        this._startHandler = (evt) => {
            const { rotation, pan } = this._config;
            const isMouse = evt.pointerType === 'mouse';
            const rotating = rotation.enabled && isMouse && evt.button === rotation.button && CustomEventUtils.matchModifiers(evt, rotation.modifiers);
            const panning = pan.enabled && isMouse && evt.button === pan.button && CustomEventUtils.matchModifiers(evt, pan.modifiers);
            if (!rotating && !panning) {
                return;
            }
            this.#rotating = rotating;
            if (this.#panning = panning) {
                // build current state from the current camera configuration
                this._currentState = OrbitCameraBehavior._buildState(this._camera);
                // reset pan offsets
                Utils.extend(this._input, Vector3D.zero());
                this._progress.x = this._progress.y = 0;
            }
            avoidHandler(evt);
            window.addEventListener('pointerup', this._endHandler, false);
            window.addEventListener('pointermove', this._moveHandler, false);
            window.addEventListener('contextmenu', this._avoidHandler, false);
        };
        this._avoidHandler = (evt) => avoidHandler(evt);
        this._endHandler = (evt) => {
            avoidHandler(evt);
            // delay the listener removals a bit (~frame)
            window.setTimeout(() => {
                window.removeEventListener('pointerup', this._endHandler);
                window.removeEventListener('pointermove', this._moveHandler);
                window.removeEventListener('contextmenu', this._avoidHandler);
            }, 20);
        };
        this._zoomHandler = (evt) => {
            const { zoom } = this._config;
            const zooming = zoom.enabled && CustomEventUtils.matchModifiers(evt, zoom.modifiers);
            if (!zooming) {
                return;
            }
            avoidHandler(evt);
            const coords = this._input;
            coords.rho -= Math.sign(evt.deltaY);
        };
        this._moveHandler = (evt) => {
            avoidHandler(evt);
            const { height, width } = this._scene.size;
            const coords = this._input;
            const size = Math.min(height, width);
            if (this.#rotating) {
                // rotation
                const f0 = -ROTATION_FACTOR * 360 / size;
                const { phi, theta } = coords;
                const { maxPolar, minPolar, maxAzimuth, minAzimuth } = this._config;
                coords.phi = (phi + evt.movementX * f0).clamp(minPolar, maxPolar);
                coords.theta = (theta + evt.movementY * f0).clamp(minAzimuth, maxAzimuth);
            }
            else if (this.#panning) {
                // pan
                const f0 = this._currentState.radius / size;
                coords.x += -evt.movementX * f0;
                coords.y += evt.movementY * f0;
            }
        };
        this._renderHandler = (_) => {
            this._update();
        };
        const scene = this._scene = (this._camera = item).stage;
        this._initialState = this._currentState = OrbitCameraBehavior._buildState(item);
        const canvas = this._canvas = scene.stage;
        canvas.addEventListener('pointerdown', this._startHandler, false);
        canvas.addEventListener('wheel', this._zoomHandler, { passive: false });
        scene.addEventListener('render', this._renderHandler, false);
    }
    static _buildState(camera) {
        const vector = Vector3D.subtract(camera.position, camera.lookAt);
        const radius = Vector3D.mag(vector);
        return { target: camera.lookAt, versor: Vector3D.unit(vector), up: camera.up, radius, position: camera.position };
    }
    /**
     * Tries to attach a new {@link OrbitCameraBehavior} to the provided element, if the element is a proper renderable and it is ready.
     * @param element
     * @returns
     */
    static tryAttach(element, config) {
        config ??= DEFAULT_CONFIGURATION;
        const canvas = findCameraStage(element)?.stage;
        if (!Utils.isNull(canvas)) {
            const { position, lookAt } = element;
            if (Utils.isNull(position) || Utils.isNull(lookAt) || Vector3D.areClose(position, lookAt)) {
                return null;
            }
            return new OrbitCameraBehavior(element, config);
        }
        else {
            return null;
        }
    }
    get configuration() {
        return this._config;
    }
    set configuration(v) {
        this._config = v;
    }
    #panning;
    #rotating;
    _getSafeAzimuth({ theta }) {
        return OrbitCameraBehavior._modAngle(theta);
    }
    _getSafePolar({ phi }) {
        return OrbitCameraBehavior._modAngle(phi);
    }
    static _modAngle(deg) {
        while (deg < 0) {
            deg += 360;
        }
        return deg % 360;
    }
    static _getViewXYZ(state) {
        const { versor, up: initUp } = state;
        const right = Vector3D.cross(versor, initUp);
        const up = Vector3D.cross(right, versor);
        Vector3D.normalize(right);
        Vector3D.normalize(up);
        return { right, up, versor };
    }
    #updating;
    _update() {
        if (this.#updating) {
            return;
        }
        this.#updating = true;
        const camera = this._camera;
        const { radius, versor, target: initTarget, up: initUp } = this._initialState;
        const { target: trackedLookAt } = this._currentState;
        const { theta, phi, rho, x, y } = this._input;
        const progress = this._progress;
        const { theta: theta_, phi: phi_, rho: rho_, x: x_, y: y_ } = progress;
        const rotated = Math.abs(phi_ - phi) > EPSILON || Math.abs(theta_ - theta) > EPSILON, zoomed = Math.abs(rho_ - rho) > EPSILON, panned = Math.abs(x - x_) > EPSILON || Math.abs(y - y_) > EPSILON;
        if (zoomed || rotated || panned) {
            const inertia = typeof this._config.inertia === 'boolean' ? (this._config.inertia ? FRICTION_FACTOR : 0) : this._config.inertia;
            const inertiaSpecified = inertia > 0 && inertia < 1;
            const { right, up } = OrbitCameraBehavior._getViewXYZ(this._initialState);
            // setup position
            let newPosition = initTarget;
            let newUp = initUp;
            let newLookAt = trackedLookAt;
            //if (zoomed) { 2
            let rho0 = rho_;
            if (inertiaSpecified) {
                rho0 += (rho - rho_) * inertia;
            }
            else {
                rho0 = rho;
            }
            progress.rho = rho0;
            const scale = Math.pow(.95, rho0 * ZOOM_FACTOR);
            const zoom = Vector3D.scale(versor, -scale * radius);
            newPosition = Vector3D.add(newPosition, zoom);
            //}
            //if (rotated) { 3
            let phi0 = phi_;
            let theta0 = theta_;
            if (inertiaSpecified) {
                phi0 += (phi - phi_) * inertia;
                theta0 += (theta - theta_) * inertia;
            }
            else {
                phi0 = phi;
                theta0 = theta;
            }
            const PHI = this._getSafePolar({ phi: progress.phi = phi0 });
            const THETA = this._getSafeAzimuth({ theta: progress.theta = theta0 });
            // pivot of the rotation matrix should be the center of the bounding sphere
            const pivot = Vector3D.negate(initTarget);
            let m = Matrix3D.translate(Matrix3D.identity(), pivot);
            const qy = Quaternion.fromAxisAngle(up, PHI);
            const qx = Quaternion.fromAxisAngle(right, THETA);
            const rot = Matrix3D.multiply(Quaternion.toRotationMatrix(qx), Quaternion.toRotationMatrix(qy));
            m = Matrix3D.multiply(m, rot);
            m = Matrix3D.translate(m, Vector3D.negate(pivot));
            newPosition = Matrix3D.transform(newPosition, m);
            newUp = Matrix3D.transform(newUp, Matrix3D.clone(m, m0 => { m0.offsetX = m0.offsetY = m0.offsetZ = 0; }));
            //}
            //if (panned){ 1
            const x1 = x_, y1 = y_;
            let x0 = x1, y0 = y1;
            if (inertiaSpecified) {
                x0 += (x - x1) * inertia;
                y0 += (y - y1) * inertia;
            }
            else {
                x0 = x;
                y0 = y;
            }
            progress.x = x0;
            progress.y = y0;
            const { right: currentRight, up: currentUp } = OrbitCameraBehavior._getViewXYZ(this._currentState);
            const r = Vector3D.scale(currentRight, x0), u = Vector3D.scale(currentUp, y0);
            const offset = Vector3D.add(r, u);
            newLookAt = Vector3D.add(trackedLookAt, offset);
            camera.lookAt = newLookAt;
            camera.position = newPosition; // Vector3D.add(newPosition, Vector3D.subtract(initTarget, newLookAt));
            camera.up = newUp;
            // notify something has changed about the target renderable
            camera.flags.push(StalePropertyFlag.Camera);
        }
        this.#updating = false;
    }
    /** Detaches and removes handlers and resources. */
    dispose() {
        const canvas = this._canvas, scene = this._scene;
        if (!Utils.isNull(canvas)) {
            canvas.removeEventListener('pointerdown', this._startHandler);
            canvas.removeEventListener('wheel', this._zoomHandler);
        }
        scene.removeEventListener('render', this._renderHandler, false);
    }
}
let PacemOrbitCameraBehaviorElement = class PacemOrbitCameraBehaviorElement extends Behaviors.PacemBehavior {
    constructor() {
        super(...arguments);
        this.#map = new Map();
        this._readyHandler = (evt) => {
            if (evt.detail.propertyName === 'isReady' && evt.detail.currentValue === true) {
                const element = evt.currentTarget;
                element.removeEventListener(PropertyChangeEventName, this._readyHandler);
                this._decorateCore(element);
            }
        };
    }
    #map;
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        switch (name) {
            case 'maxAzimuth':
            case 'minAzimuth':
            case 'inertia':
            case 'zoomControl':
            case 'panControl':
            case 'rotationControl':
                const config = this._buildConfig();
                const items = this.#map;
                for (let tr of items.values()) {
                    tr.configuration = config;
                }
                break;
        }
    }
    disconnectedCallback() {
        const items = this.#map;
        for (let tr of items.values()) {
            tr.dispose();
        }
        items.clear();
        super.disconnectedCallback();
    }
    _buildConfig() {
        return Utils.extend({}, DEFAULT_CONFIGURATION, {
            maxAzimuth: this.maxAzimuth ?? Infinity,
            minAzimuth: this.minAzimuth ?? -Infinity,
            minPolar: this.minPolar ?? -Infinity,
            maxPolar: this.maxPolar ?? Infinity,
            inertia: this.inertia ?? false,
            zoom: this.zoomControl ?? DEFAULT_CONFIGURATION.zoom,
            pan: this.panControl ?? DEFAULT_CONFIGURATION.pan,
            rotation: this.rotationControl ?? DEFAULT_CONFIGURATION.rotation,
        });
    }
    _decorateCore(element) {
        const behavior = OrbitCameraBehavior.tryAttach(element, this._buildConfig());
        if (!Utils.isNull(behavior)) {
            this.#map.set(element, behavior);
        }
    }
    decorate(element) {
        if (element.isConnected) {
            this._decorateCore(element);
        }
        else {
            element.addEventListener(PropertyChangeEventName, this._readyHandler, false);
        }
    }
    undecorate(element) {
        const map = this.#map;
        if (map.has(element)) {
            map.get(element).dispose();
            map.delete(element);
        }
    }
};
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemOrbitCameraBehaviorElement.prototype, "maxAzimuth", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemOrbitCameraBehaviorElement.prototype, "maxPolar", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemOrbitCameraBehaviorElement.prototype, "minAzimuth", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemOrbitCameraBehaviorElement.prototype, "minPolar", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemOrbitCameraBehaviorElement.prototype, "zoomControl", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemOrbitCameraBehaviorElement.prototype, "rotationControl", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemOrbitCameraBehaviorElement.prototype, "panControl", void 0);
__decorate([
    Watch({ emit: false, converter: BooleanOrNumberConverter })
], PacemOrbitCameraBehaviorElement.prototype, "inertia", void 0);
PacemOrbitCameraBehaviorElement = __decorate([
    CustomElement({ tagName: P + '-' + TAG_MIDDLE_NAME + '-orbit-camera' })
], PacemOrbitCameraBehaviorElement);
export { PacemOrbitCameraBehaviorElement };
